import { EmissionsRecordsTab } from "#batteries-included-components/Tabs/Emissions/EmissionsRecordsTab";
import { EstimationMethodsTab } from "#batteries-included-components/Tabs/EstimationMethod/ListTab";
import { useNavigate, useSearchParams } from "#routers/hooks";
import { EquipmentRecordsTab } from "#routes/organization/equipment/[equipmentId]/EquipmentRecordsTab";
import EditEquipmentDialog from "#src/batteries-included-components/Dialogs/EditEquipmentDialog";
import { UpdateEntityStatusDialog } from "#src/batteries-included-components/Dialogs/UpdateEntityStatusDialog";
import { EquipmentViewFilterArea } from "#src/batteries-included-components/FilterAreas/AssetsFilterAreas";
import { IsFeatureAvailable } from "#src/batteries-included-components/Layouts/Authorization/IsFeatureAvailable";
import { PageErrorContent } from "#src/batteries-included-components/Layouts/Errors/PageError";
import { RoutingLink } from "#src/batteries-included-components/RoutingLink";
import { EntityMeasurementsTab } from "#src/batteries-included-components/Tabs/EntityMeasurementsTab/EntityMeasurementsTab";
import { useUpdateAssetStatusAsync } from "#src/components/hooks/adapters/useAssets";
import { useListFlows } from "#src/components/hooks/adapters/useFlows";
import { useListWorkflows } from "#src/components/hooks/adapters/useWorkflows";
import { useCreateEditMetaSegments } from "#src/components/hooks/useCreateEditMetaSegments";
import { useIsFeatureAvailable } from "#src/contexts/AuthenticatedContext.helpers";
import useLocalization from "#src/hooks/useLocalization";
import { useStickyState } from "#src/hooks/useStickyState";
import { useStorageKey } from "#src/hooks/useStorageKey";
import { linkToEquipmentDetail } from "#src/Routers/links";
import { EquipmentListRoute } from "#src/routes/organization/equipment";
import { EquipmentDetailRoute } from "#src/routes/organization/equipment/[equipmentId]";
import { linkToEstimationMethodCreate } from "#src/routes/organization/equipment/[equipmentId]/estimation-method/create";
import { AssociatedFormSubmissionsTab } from "#src/routes/organization/facilities/[facilityId]/AssociatedFormSubmissionsTab";
import { getAssetFilters } from "#src/routes/organization/facilities/[facilityId]/AssociatedWorkflows.helpers";
import { AssociatedWorkflowsTab } from "#src/routes/organization/facilities/[facilityId]/AssociatedWorkflowsTab";
import { useBreadcrumbsFromRoute } from "#src/utils/route";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Button,
  Dialog,
  DropdownMenu,
  generateDocumentTitle,
  Icon,
  Page,
  Pill,
  Tab,
  useToast,
} from "@validereinc/common-components";
import {
  AssetType,
  EquipmentAdapter,
  FormSubmissionAdapter,
  isResponseErrorType,
  ResourceDefinitions,
  Resources,
} from "@validereinc/domain";
import {
  getYearMonthFromDateRange,
  HTTPResponseError,
  toTitleCaseString,
  type HTTPNotFoundErrorCtx,
} from "@validereinc/utilities";
import * as PropTypes from "prop-types";
import React, { useState } from "react";
import { useParams } from "react-router-dom";
import { Titled } from "react-titled";
import { ChangeLogTab } from "./ChangeLogTab";
import EquipmentDetailOverviewTab from "./EquipmentDetailOverviewTab";

const EquipmentDetail = () => {
  const { equipmentId } = useParams<{ equipmentId: string }>();
  const queryClient = useQueryClient();
  const [isUpdating, setIsUpdatingStatus] = useState(false);
  const [
    isUpdateEquipmentStatusDialogOpen,
    setIsUpdateEquipmentStatusDialogOpen,
  ] = useState(false);

  const [searchParams, setSearchParams] = useSearchParams<{
    tab: string;
    form_category_name: string;
  }>();
  const { localize, isLoading: isLoadingLocalization } = useLocalization();
  const { toast } = useToast();
  const [areFlowsEnabled, isLoadingAreFlowsEnabled] = useIsFeatureAvailable({
    featureFlagQuery: "core:flows",
    permissionQuery: {
      $and: ["flows:read"],
    },
  });
  const [areFormsEnabled, isLoadingAreFormsEnabled] = useIsFeatureAvailable({
    featureFlagQuery: "core:forms",
    permissionQuery: {
      $and: [
        "form_categories:read",
        "form_schemas:read",
        "form_submissions:read",
      ],
    },
  });
  const [areWorkflowsEnabled, isLoadingAreWorkflowsEnabled] =
    useIsFeatureAvailable({
      featureFlagQuery: "core:workflows",
      permissionQuery: {
        $and: ["workflows:read", "workflow_templates:read"],
      },
    });
  const [isEstimationMethodsEnabled, isLoadingIsEstimationMethodsEnabled] =
    useIsFeatureAvailable({
      featureFlagQuery: "core:estimation_methods",
      permissionQuery: "calculator_results:read",
    });
  const [isChangeLogAvailable, isLoadingIsChangeLogAvailable] =
    useIsFeatureAvailable({
      featureFlagQuery: "core:activity_logs",
      permissionQuery: "activities:read",
    });
  const [assetsSharedViewConfigStorageKey, groupByTablesStateStorageKey] =
    useStorageKey(
      ["view-configuration", "group-by-tables-state"],
      "assets-shared"
    );
  const [isDeleteConfirmationOpen, setIsDeleteConfirmationOpen] =
    useState<boolean>(false);
  const [assetIdToEdit, setAssetIdToEdit] = useState<string | undefined>();
  const navigate = useNavigate();
  const [filters] = useStickyState<{
    period?: { from: string; to: string };
  }>({}, assetsSharedViewConfigStorageKey);

  const activePeriod = getYearMonthFromDateRange(filters?.period) ?? "";

  const {
    data: equipmentDetail,
    error,
    isLoading: isLoadingEquipmentDetail,
  } = useQuery({
    queryKey: [
      "equipment",
      {
        id: equipmentId,
        meta: {
          period: activePeriod,
        },
      },
    ],
    queryFn: () =>
      EquipmentAdapter.getOne({
        id: equipmentId,
        meta: {
          period: activePeriod,
        },
      }),
    select: (resp) => resp.data,
    meta: {
      hideErrorToasts: true,
      errorCtx: {
        resource: localize("Equipment"),
        resourceId: equipmentId,
      } satisfies HTTPNotFoundErrorCtx,
    },
  });
  const [breadcrumbs] = useBreadcrumbsFromRoute(EquipmentDetailRoute, {
    "/equipment": {
      title: localize(`${Resources.EQUIPMENT}_plural`),
    },
    "/detail": {
      title: equipmentDetail?.name ?? equipmentId,
    },
  });
  const relatedFlowsQuery = useListFlows(
    {
      page: 1,
      pageSize: 1,
      filters: {
        // @ts-expect-error TODO: fix filter typing
        $or: [
          { associated_equipment_id: equipmentId },
          { downstream_equipment_id: equipmentId },
          { upstream_equipment_id: equipmentId },
        ] as any,
        isAlreadyFormatted: true,
      },
    },
    {
      enabled: areFlowsEnabled && !!equipmentId,
    }
  );
  const relatedFormSubmissionsQuery = useQuery({
    queryKey: [Resources.FORM_SUBMISSION, Resources.EQUIPMENT, equipmentId],
    queryFn: () => {
      return FormSubmissionAdapter.getList({
        filters: {
          related_entity_id: equipmentId,
        },
      });
    },
    enabled: areFormsEnabled && !!equipmentId,
  });
  const relatedWorkflowsQuery = useListWorkflows(
    {
      page: 1,
      pageSize: 1,
      filters: {
        ...getAssetFilters(Resources.EQUIPMENT, { assetId: equipmentId }),
      },
    },
    {
      enabled: areWorkflowsEnabled && !!equipmentId,
    }
  );
  const createEditMetaSegments = useCreateEditMetaSegments({
    editedAt: equipmentDetail?.updated_at,
    createdAt: equipmentDetail?.created_at,
  });

  const deleteMutation = useMutation({
    mutationFn: (params: Parameters<typeof EquipmentAdapter.deleteOne>[0]) => {
      return EquipmentAdapter.deleteOne(params);
    },
    onSuccess: (_, params) => {
      toast.push({
        intent: "success",
        description: `Successfully deleted ${localize(Resources.EQUIPMENT)}${params.previousData?.name ? ` ${params.previousData?.name}.` : "."}`,
      });
      setIsDeleteConfirmationOpen(false);
      navigate(EquipmentListRoute.toLinkParts());
    },
    onError: (error) => {
      if (
        error instanceof HTTPResponseError &&
        error.responseBodyJson &&
        isResponseErrorType(error.responseBodyJson)
      ) {
        toast.push({
          intent: "error",
          description: `Unable to delete ${localize(Resources.EQUIPMENT)} due to a server error: ${error.responseBodyJson.error}`,
        });
      } else {
        toast.push({
          intent: "error",
          description: `Unable to delete ${localize(Resources.EQUIPMENT)}. An unknown error occurred.`,
        });
      }
    },
  });

  const [updateEquipmentStatusJobStorageKey] = useStorageKey(
    ["async-job"],
    `update-equipment-status-${equipmentId}`
  );

  const updateStatusMutation = useUpdateAssetStatusAsync({
    jobKey: updateEquipmentStatusJobStorageKey,
    assetId: equipmentId,
    setIsUpdating: setIsUpdatingStatus,
    invalidate: () => {
      queryClient.invalidateQueries({
        queryKey: [
          "equipment",
          {
            id: equipmentId,
            meta: {
              period: activePeriod,
            },
          },
        ],
      });
      queryClient.invalidateQueries({
        queryKey: [Resources.ACTIVITY],
      });
    },
  });
  const handleChangeEquipmentStatusClick = () =>
    setIsUpdateEquipmentStatusDialogOpen(true);

  const onClickConfigureEquipment = () => setAssetIdToEdit(equipmentId);

  const handleActiveTabKeyChange = (tabKey: string) => {
    setSearchParams({ tab: tabKey });
  };

  const actionRow = (
    <>
      <IsFeatureAvailable
        key="edit-access-policy"
        featureFlagQuery="core:equipment"
        permissionQuery="equipment:write"
        fallbackChildren={null}
      >
        <Button onClick={onClickConfigureEquipment}>Edit</Button>
      </IsFeatureAvailable>
      <IsFeatureAvailable
        key="change-status-access-policy"
        featureFlagQuery="core:equipment"
        permissionQuery="equipment:write"
        fallbackChildren={null}
      >
        <Button
          isLoading={isUpdating}
          onClick={handleChangeEquipmentStatusClick}
        >
          Change Status
        </Button>
      </IsFeatureAvailable>
      <IsFeatureAvailable
        key="more-access-policy"
        featureFlagQuery="core:equipment"
        permissionQuery="equipment:delete"
        fallbackChildren={null}
      >
        <DropdownMenu
          key="more"
          options={[
            {
              label: "Delete",
              variant: "danger",
              isDisabled: deleteMutation.isLoading,
              slotLeft: ({ iconProps }) => (
                <Icon
                  {...iconProps}
                  variant="trash"
                />
              ),
              onClick: () => setIsDeleteConfirmationOpen(true),
            },
          ]}
        >
          <Button
            icon="dots-three-vertical"
            variant="outline"
          />
        </DropdownMenu>
      </IsFeatureAvailable>
    </>
  );

  const hasAssociatedResources =
    !!relatedFormSubmissionsQuery.data?.total_entries ||
    !!relatedWorkflowsQuery.data?.total_entries ||
    !!relatedFlowsQuery.data?.total_entries;

  return (
    <>
      <Page
        category={localize("Equipment")}
        isLoading={
          isLoadingEquipmentDetail ||
          isLoadingLocalization ||
          isLoadingAreFormsEnabled ||
          isLoadingIsChangeLogAvailable ||
          isLoadingIsEstimationMethodsEnabled ||
          isLoadingAreWorkflowsEnabled ||
          isLoadingAreFlowsEnabled
        }
        title={
          equipmentDetail ? (
            <>
              {equipmentDetail.name}&nbsp;
              <Pill
                variant={
                  equipmentDetail.status === "active" ? "success" : "default"
                }
                hasDot={false}
              >
                {toTitleCaseString(equipmentDetail.status, {
                  intent: "ui",
                  exceptions: ["oil-and-gas"],
                })}
              </Pill>
            </>
          ) : (
            "Detail"
          )
        }
        breadcrumbs={breadcrumbs}
        actionRow={actionRow}
        onActiveTabKeyChange={handleActiveTabKeyChange}
        activeTabKey={
          !isChangeLogAvailable && searchParams?.tab === "change-log"
            ? "overview"
            : (searchParams?.tab ?? "overview")
        }
        error={error}
        onErrorRender={({ error }) => <PageErrorContent error={error} />}
        renderMeta={
          createEditMetaSegments.length === 0
            ? undefined
            : ({ MetaSegments }) => (
                <MetaSegments values={createEditMetaSegments} />
              )
        }
      >
        <Tab
          tabKey="overview"
          title="Overview"
        >
          <Titled
            title={generateDocumentTitle(
              breadcrumbs.at(-1)?.title ?? EquipmentDetailRoute.title,
              localize("equipment_plural")
            )}
          >
            <>
              {equipmentDetail && (
                <EquipmentDetailOverviewTab
                  equipmentDetail={equipmentDetail}
                  viewFilterAreaSlot={
                    <EquipmentViewFilterArea
                      viewConfigStorageKey={assetsSharedViewConfigStorageKey}
                    />
                  }
                  viewConfigStorageKey={assetsSharedViewConfigStorageKey}
                  groupByTablesStateStorageKey={groupByTablesStateStorageKey}
                  filterKey={""}
                />
              )}
            </>
          </Titled>
        </Tab>

        {isEstimationMethodsEnabled ? (
          <Tab
            tabKey="emissions"
            title={ResourceDefinitions.emissions.label.plural}
          >
            <Titled
              title={generateDocumentTitle(
                `${
                  breadcrumbs.at(-1)?.title ?? EquipmentDetailRoute.title
                } ${ResourceDefinitions.emissions.label.plural}`,
                breadcrumbs.at(-2)?.title
              )}
            >
              <EmissionsRecordsTab
                assetType={AssetType.EQUIPMENT}
                assetId={equipmentId}
              />
            </Titled>
          </Tab>
        ) : null}

        <Tab
          tabKey="records"
          title={ResourceDefinitions.record.label.plural}
        >
          <Titled
            title={generateDocumentTitle(
              `${
                breadcrumbs.at(-1)?.title ?? EquipmentDetailRoute.title
              } ${ResourceDefinitions.record.label.plural}`,
              breadcrumbs.at(-2)?.title
            )}
          >
            <EquipmentRecordsTab equipmentId={equipmentId} />
          </Titled>
        </Tab>

        <Tab
          tabKey="measurements"
          title={ResourceDefinitions.measurement.label.plural}
        >
          <Titled
            title={generateDocumentTitle(
              `${
                breadcrumbs.at(-1)?.title ?? EquipmentDetailRoute.title
              } ${ResourceDefinitions.measurement.label.plural}`,
              breadcrumbs.at(-2)?.title
            )}
          >
            <EntityMeasurementsTab
              entityId={equipmentId}
              entityType={AssetType.EQUIPMENT}
            />
          </Titled>
        </Tab>

        {isEstimationMethodsEnabled ? (
          <Tab
            tabKey="estimationMethods"
            title={ResourceDefinitions.estimation_method.label.plural}
          >
            <Titled
              title={generateDocumentTitle(
                `${
                  breadcrumbs.at(-1)?.title ?? EquipmentDetailRoute.title
                } ${ResourceDefinitions.estimation_method.label.plural}`,
                breadcrumbs.at(-2)?.title
              )}
            >
              <EstimationMethodsTab
                isWithinEntityDetail
                entityId={equipmentId}
                entityType={AssetType.EQUIPMENT}
                onAdd={() =>
                  navigate({
                    pathname: linkToEstimationMethodCreate(equipmentId),
                  })
                }
              />
            </Titled>
          </Tab>
        ) : null}

        {areFormsEnabled ? (
          <Tab
            tabKey={Resources.FORM_SUBMISSION}
            title="Forms"
          >
            <Titled
              title={generateDocumentTitle(
                `${
                  breadcrumbs.at(-1)?.title ?? EquipmentDetailRoute.title
                } ${ResourceDefinitions.form_submission.label.plural}`,
                breadcrumbs.at(-2)?.title
              )}
            >
              <AssociatedFormSubmissionsTab
                resourceId={equipmentId}
                resourceType={Resources.EQUIPMENT}
                defaultFormCategoryName={searchParams.form_category_name}
              />
            </Titled>
          </Tab>
        ) : null}

        {areWorkflowsEnabled ? (
          <Tab
            tabKey={Resources.WORKFLOW}
            title={ResourceDefinitions.workflow.label.plural}
          >
            <Titled
              title={generateDocumentTitle(
                `${
                  breadcrumbs.at(-1)?.title ?? EquipmentDetailRoute.title
                } ${ResourceDefinitions.workflow.label.plural}`,
                breadcrumbs.at(-2)?.title
              )}
            >
              <AssociatedWorkflowsTab
                resourceId={equipmentId}
                resourceType={Resources.EQUIPMENT}
              />
            </Titled>
          </Tab>
        ) : null}

        <Tab
          tabKey="change-log"
          title={ResourceDefinitions.activity.label.singular}
          isDisabled={!isChangeLogAvailable}
          iconVariant={!isChangeLogAvailable ? "lock" : undefined}
        >
          <Titled
            title={generateDocumentTitle(
              `${
                breadcrumbs.at(-1)?.title ?? EquipmentDetailRoute.title
              } ${ResourceDefinitions.activity.label.singular}`,
              breadcrumbs.at(-2)?.title
            )}
          >
            {isChangeLogAvailable ? (
              <ChangeLogTab resourceId={equipmentId} />
            ) : null}
          </Titled>
        </Tab>
      </Page>
      <Dialog
        isOpen={isDeleteConfirmationOpen}
        onClose={() => setIsDeleteConfirmationOpen(false)}
        title={`Delete ${toTitleCaseString(localize(Resources.EQUIPMENT), { intent: "ui" })}`}
        actionRow={[
          <Button
            key="confirm"
            variant="error"
            onClick={() =>
              deleteMutation.mutate({
                id: equipmentId,
                previousData: equipmentDetail,
              })
            }
            isLoading={deleteMutation.isLoading}
            disabled={hasAssociatedResources}
          >
            Delete
          </Button>,
        ]}
      >
        {hasAssociatedResources ? (
          <>
            You cannot delete {equipmentDetail?.name} until the following
            associated resources are deleted:
            <ul style={{ listStyle: "none", padding: 0, marginBlock: 8 }}>
              {relatedFlowsQuery.data?.total_entries ? (
                <li>
                  <RoutingLink
                    to={{
                      pathname: linkToEquipmentDetail(equipmentId),
                      search: "?tab=overview",
                    }}
                    onClickCapture={() => setIsDeleteConfirmationOpen(false)}
                  >
                    {relatedFlowsQuery.data.total_entries}{" "}
                    {localize(`${Resources.FLOW}_plural`)}
                  </RoutingLink>
                </li>
              ) : null}
              {relatedWorkflowsQuery.data?.total_entries ? (
                <li>
                  <RoutingLink
                    to={{
                      pathname: linkToEquipmentDetail(equipmentId),
                      search: `?tab=${Resources.WORKFLOW}`,
                    }}
                    onClickCapture={() => setIsDeleteConfirmationOpen(false)}
                  >
                    {relatedWorkflowsQuery.data?.total_entries} Workflows
                  </RoutingLink>
                </li>
              ) : null}
              {relatedFormSubmissionsQuery.data?.total_entries ? (
                <li>
                  <RoutingLink
                    to={{
                      pathname: linkToEquipmentDetail(equipmentId),
                      search: `?tab=${Resources.FORM_SUBMISSION}`,
                    }}
                    onClickCapture={() => setIsDeleteConfirmationOpen(false)}
                  >
                    {relatedFormSubmissionsQuery.data?.total_entries} Form
                    Submissions
                  </RoutingLink>
                </li>
              ) : null}
            </ul>
            Please delete them and return here to delete this{" "}
            {localize(Resources.EQUIPMENT)}.
          </>
        ) : (
          <>
            Are you sure you want to delete this {localize(Resources.EQUIPMENT)}
            ? This will also delete its associated Estimation Methods and
            Records. This action cannot be undone.
          </>
        )}
      </Dialog>
      <EditEquipmentDialog
        equipmentId={assetIdToEdit}
        onClose={() => setAssetIdToEdit(undefined)}
        defaultPeriod={activePeriod}
      />
      <UpdateEntityStatusDialog
        isOpen={isUpdateEquipmentStatusDialogOpen}
        onClose={() => {
          setIsUpdateEquipmentStatusDialogOpen(false);
        }}
        onSubmit={updateStatusMutation.handleUpdateAssetStatusAsync}
        itemCount={1}
        subject={{
          plural: ResourceDefinitions.equipment.label.plural,
          singular: ResourceDefinitions.equipment.label.singular,
        }}
        warningMessage="This will inactivate all associated estimation methods. Inactivating estimation methods will delete any associated results for the provided time range."
      />
    </>
  );
};

EquipmentDetail.propTypes = {
  breadcrumbs: PropTypes.array.isRequired,
  match: PropTypes.object.isRequired,
};

export default EquipmentDetail;
